"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateInverseFuturePoint = exports.calculateLinearFuturePoint = exports.calculateFuturePointsForIndexX = exports.getLinearFuturePnl = exports.getPerpPnlOverDomain = exports.getFuturePnlForDomain = void 0;
const types_1 = require("../types");
const calculationUtils_1 = require("./calculationUtils");
// ----------------------------------------- //
// ---------------- PUBLIC ----------------- //
// ----------------------------------------- //
/**
 * Calculates pnl for a future over a domain.
 *
 * @param {number[]} domain - Array of values that represent the x axis for calculation.
 * @param currencyType
 * @param {number} r - The interest rate
 * @param {number} t - Time to expiry
 * @param {Position} position - Position that we calculate the pnl for
 * @returns {{todayLine: number[],expireLine: number[] }}
 */
const getFuturePnlForDomain = (domain, currencyType, r, t, position, isOffsetActive, markPrice) => {
    let todayLine = [];
    let expireLine = [];
    for (const x of domain) {
        const amount = position.amount;
        const averagePrice = position.averagePrice;
        const entryPrice = isOffsetActive ? markPrice : averagePrice;
        const { expirePt, todayPt } = (0, exports.calculateFuturePointsForIndexX)(currencyType, x, r, t, entryPrice, amount);
        todayLine.push(todayPt);
        expireLine.push(expirePt);
    }
    return {
        todayLine,
        expireLine,
        volatilitySmile: [],
    };
};
exports.getFuturePnlForDomain = getFuturePnlForDomain;
const getPerpPnlOverDomain = (domain, currencyType, position, ticker, isOffsetActive) => {
    let todayLine = [];
    let expireLine = [];
    const factor = ticker.markPrice / ticker.indexPrice;
    for (const x of domain) {
        const amount = position.amount;
        const averagePrice = position.averagePrice;
        const entryPrice = isOffsetActive ? ticker.markPrice : averagePrice;
        const { todayPt, expirePt } = calculatePerpetualPointsIndexX(currencyType, entryPrice, x * factor, amount);
        todayLine.push(todayPt);
        expireLine.push(expirePt);
    }
    return {
        todayLine,
        expireLine,
        volatilitySmile: [],
    };
};
exports.getPerpPnlOverDomain = getPerpPnlOverDomain;
const getLinearFuturePnl = (domain, currencyType, position, ticker, isOffsetActive, usdUsdcIndex) => {
    let todayLine = [];
    let expireLine = [];
    // Its the interest rate for perpetuals
    const factor = ticker.markPrice / ticker.indexPrice;
    for (const x of domain) {
        const amount = position.amount;
        const averagePrice = position.averagePrice;
        const entryPrice = isOffsetActive ? ticker.markPrice : averagePrice;
        const { todayPt, expirePt } = (0, exports.calculateLinearFuturePoint)(currencyType, entryPrice, x * factor, amount);
        todayLine.push(todayPt * usdUsdcIndex);
        expireLine.push(expirePt * usdUsdcIndex);
    }
    return {
        todayLine,
        expireLine,
    };
};
exports.getLinearFuturePnl = getLinearFuturePnl;
// ----------------------------------------- //
// ---------------- PRIVATE ---------------- //
// ----------------------------------------- //
/**
 * Calculates the pnl for a future position.
 *
 * @param currency
 * @param x price that we are calculating for
 * @param r interest rate
 * @param t time to expiry
 * @param averagePrice
 * @param amount Amount is in usd for futures
 * @returns {expirePt, todayPt} Expected pnl for the price x at expiry and today.
 */
const calculateFuturePointsForIndexX = (currencyType, x, r, t, averagePrice, amount) => {
    let expirePt = (0, exports.calculateInverseFuturePoint)(currencyType, averagePrice, x, amount);
    const exitPrice = x * Math.exp(r * t);
    let todayPt = (0, exports.calculateInverseFuturePoint)(currencyType, averagePrice, exitPrice, amount);
    if (t <= 0) {
        return {
            expirePt: expirePt,
            todayPt: expirePt,
        };
    }
    return {
        expirePt: (0, calculationUtils_1.sanitizeNumber)(expirePt),
        todayPt: (0, calculationUtils_1.sanitizeNumber)(todayPt),
    };
};
exports.calculateFuturePointsForIndexX = calculateFuturePointsForIndexX;
/**
 * Calculates the pnl for a perpetual position.
 */
const calculatePerpetualPointsIndexX = (currencyType, averagePrice, x, amount) => {
    let expirePt = currencyType === types_1.CurrencyType.CRYPTO
        ? amount / averagePrice - amount / x
        : ((x - averagePrice) * amount) / averagePrice;
    return {
        expirePt: (0, calculationUtils_1.sanitizeNumber)(expirePt),
        todayPt: (0, calculationUtils_1.sanitizeNumber)(expirePt),
    };
};
/**
 * Calculates the pnl for a linear future.
 */
const calculateLinearFuturePoint = (currencyType, entryPrice, exitPrice, amount) => {
    const expirePt = currencyType === types_1.CurrencyType.FIAT
        ? (exitPrice - entryPrice) * amount
        : ((exitPrice - entryPrice) * amount) / exitPrice;
    return {
        expirePt: (0, calculationUtils_1.sanitizeNumber)(expirePt),
        todayPt: (0, calculationUtils_1.sanitizeNumber)(expirePt),
    };
};
exports.calculateLinearFuturePoint = calculateLinearFuturePoint;
/**
 * Calculates the pnl for a inverse future.
 */
const calculateInverseFuturePoint = (currencyType, entryPrice, exitPrice, amount) => {
    return currencyType === types_1.CurrencyType.CRYPTO
        ? amount / entryPrice - amount / exitPrice
        : ((exitPrice - entryPrice) * amount) / entryPrice;
};
exports.calculateInverseFuturePoint = calculateInverseFuturePoint;
